import { Action, configureStore, ThunkDispatch } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { caseReducer } from './components/pages/Case/Case.slice';
import { coiFormManagementReducer } from './components/pages/CoiFormManagement/CoiFormManagement.slice';
import { userReducer } from './components/pages/Users/User.slice';
import { legacyReducers } from './reducers';
import { categoriesReducer } from './reducers/Categories.slice';
import { reportingReducer } from './reducers/Reporting.slice';
import { tagsReducer } from './reducers/Tags.slice';
import {
  MCCTypes,
  OpenReportTypes,
  OrganisationTypes,
  ReportTypes,
  State,
} from './types';

const sentryRestrictedReduxActions = [
  MCCTypes.EDIT_EVENT,
  MCCTypes.UPDATE_SUCCESS,
  MCCTypes.UPDATE_FAILURE,
  MCCTypes.FETCH_ONE,
  ReportTypes.FETCH_ONE,
  ReportTypes.UPDATE_ISSUE_TYPE,
  ReportTypes.MESSAGES,
  OrganisationTypes.FETCH_AUTH_ADMIN,
  OpenReportTypes.MESSAGE,
  OpenReportTypes.MESSAGES,
  OpenReportTypes.READ_MESSAGE,
  ReportTypes.MESSAGE,
  ReportTypes.MESSAGES,
  ...Object.keys(OpenReportTypes),
];

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  stateTransformer: (state: State) => ({
    ...state,
    globalUsers: 'REDACTED',
    globalReports: 'REDACTED',
    manualCaseCreation: {
      ...state.manualCaseCreation,
      mccReducer: {
        ...state.manualCaseCreation.mccReducer,
        currentReport: 'REDACTED',
        currentEvent: 'REDACTED',
        events: 'REDACTED',
      },
    },
    openReporting: 'REDACTED',
    login: 'REDACTED',
    organisation: 'REDACTED',
  }),
  actionTransformer: (action) => {
    if (sentryRestrictedReduxActions.includes(action.type)) {
      return null;
    }
    return action;
  },
});

export const configureLiveStore = (options = {}) => {
  return configureStore({
    // TODO investigate type mismatch on action type between toolkit and legacy reducers
    // @ts-ignore
    reducer: {
      ...legacyReducers,
      case: caseReducer,
      categories: categoriesReducer,
      user: userReducer,
      tags: tagsReducer,
      reporting: reportingReducer,
      coiFormManagement: coiFormManagementReducer,
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }),
    // @ts-ignore
    enhancers: (getDefaultEnhancers) =>
      getDefaultEnhancers().concat(sentryReduxEnhancer),
    ...options,
  });
};

const store = configureLiveStore();

export default store;

export type TRootState = ReturnType<typeof store.getState>;

export type ThunkAppDispatch = ThunkDispatch<TRootState, void, Action>;
export type AppDispatch = typeof store.dispatch;
